import { cn } from '@/lib/utils';
import type { PostGridObjectStatus } from '@/services/api/generated/postgridService';

import { CancelButton } from './CancelButton';
import { StatusBadge } from './PostgridStatusBadge';
import { ProgressTestButton } from './ProgressTestButton';

interface StatusIndicatorWithActionsProps {
  // Status
  status?: PostGridObjectStatus;
  isTest?: boolean;

  // Entity context
  entityType?: 'letter' | 'cheque' | 'contact' | 'item';
  entityName?: string;
  entityId?: string;

  // Actions
  canCancel?: boolean;
  canProgressTest?: boolean;
  onCancel?: () => void;
  onProgressTest?: () => void;

  // Loading states
  isCancelLoading?: boolean;
  isProgressLoading?: boolean;

  // Layout
  layout?: 'inline' | 'stacked' | 'justified';
  spacing?: 'tight' | 'normal' | 'loose';
  actionSize?: 'sm' | 'lg';

  // Styling
  className?: string;
  statusClassName?: string;
  actionsClassName?: string;
}

/**
 * Combined component that displays status indicators with contextual actions.
 * Provides consistent layout patterns for status + action combinations.
 */
export const StatusIndicatorWithActions: React.FC<
  StatusIndicatorWithActionsProps
> = ({
  status,
  isTest = false,
  entityType = 'item',
  entityName,
  entityId,
  canCancel = false,
  canProgressTest = false,
  onCancel,
  onProgressTest,
  isCancelLoading = false,
  isProgressLoading = false,
  layout = 'inline',
  spacing = 'normal',
  actionSize = 'sm',
  className,
  statusClassName,
  actionsClassName,
}) => {
  const hasActions =
    (canCancel && onCancel) || (canProgressTest && onProgressTest && isTest);

  if (!hasActions) {
    return (
      <StatusBadge status={status} className={cn(statusClassName, className)} />
    );
  }

  const spacingClasses = {
    tight: 'gap-2',
    normal: 'gap-3',
    loose: 'gap-4',
  };

  const layoutClasses = {
    inline: 'flex items-center',
    stacked: 'flex flex-col items-start',
    justified: 'flex items-center justify-between w-full',
  };

  const renderActions = () => {
    const actions = [];

    if (canProgressTest && onProgressTest && isTest) {
      actions.push(
        <ProgressTestButton
          key="progress"
          onProgress={onProgressTest}
          isLoading={isProgressLoading}
          isTest={isTest}
          entityType={entityType}
          size={actionSize}
          currentStatus={status}
        />,
      );
    }

    if (canCancel && onCancel) {
      actions.push(
        <CancelButton
          key="cancel"
          onConfirm={onCancel}
          isLoading={isCancelLoading}
          entityType={entityType}
          entityName={entityName}
          entityId={entityId}
          size={actionSize}
        />,
      );
    }

    return actions;
  };

  return (
    <div
      className={cn(layoutClasses[layout], spacingClasses[spacing], className)}
    >
      <StatusBadge status={status} className={statusClassName} />

      <div
        className={cn(
          'flex items-center gap-2',
          layout === 'stacked' && 'mt-2',
          actionsClassName,
        )}
      >
        {renderActions()}
      </div>
    </div>
  );
};
